'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'

/** Small clipboard control for hex constants. Announces the result to assistive tech. */
export function CopyButton({ value, label }: { value: string; label: string }) {
	const [copied, setCopied] = useState(false)

	useEffect(() => {
		if (!copied) return
		const t = setTimeout(() => setCopied(false), 1800)
		return () => clearTimeout(t)
	}, [copied])

	const onCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(value)
			setCopied(true)
		} catch {
			setCopied(false)
		}
	}, [value])

	return (
		<button
			type="button"
			onClick={onCopy}
			aria-label={copied ? `${label} copied` : `Copy ${label}`}
			className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
		>
			{copied ? (
				<Check className="size-3.5 text-success" aria-hidden="true" />
			) : (
				<Copy className="size-3.5" aria-hidden="true" />
			)}
			<span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
		</button>
	)
}
